import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {Card, DataStatus, Parameter} from 'platform/components';
import {Box, Show, Stack, Text, VStack} from 'platform/foundation';
import {formatPhoneNumber, useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useParams} from 'react-router-dom';

import {isEmpty, isNil, isNotNil, propOr} from 'ramda';

import {
  TelephonyCallDetail as TelephonyCallDetailModel,
  useGetTelephonyCallDetailQuery,
} from '@dms/api/settings';
import {TelephonyCallNoteEditor} from '@dms/features/telephony-call';
import i18n from '@dms/i18n';
import {customerRoutes, testIds} from '@dms/routes';
import {
  EntityHeader,
  EntityHeaderAction,
  EntityHeaderFlag,
  ErrorPage,
  formatCallDuration,
  getTelephonyCallOutcomeLabel,
  isMissedTelephonyCallOutcome,
  Page,
  usePermissions,
} from '@dms/shared';

import {buildArray, composePath, parseDate, TestIdProps, useNavigate} from 'shared/web';

import {CallTimeline} from '../../components/CallTimeline/CallTimeline';
import {CallCustomerCard} from './components/CallCustomerCard';
import {CallLinkedRecordsCard} from './components/CallLinkedRecordsCard';

const NOT_FOUND_STATUS = 404;
const MAIN_COLUMN_FLEX = 7;
const SIDE_COLUMN_FLEX = 5;

const isNotFoundError = (error: unknown) => propOr(null, 'status', error) === NOT_FOUND_STATUS;

/** A stored call: who, when, how it ended, which customer and records it belongs to, the note. */
export function TelephonyCallDetail() {
  const {id: callId} = useParams<{id: string}>();
  const {
    data: call,
    isLoading,
    isError,
    error,
  } = useGetTelephonyCallDetailQuery(isNotNil(callId) ? {callId} : skipToken);

  if (isNil(callId) || isNotFoundError(error)) {
    return <ErrorPage message={i18n.t('page.telephony.callDetail.notFound')} />;
  }

  return (
    <>
      <Helmet title={i18n.t('page.telephony.callDetail.title')} />
      <DataStatus isLoading={isLoading} isError={isError} minHeight={50}>
        <Show when={call}>{(loadedCall) => <TelephonyCallDetailLoaded call={loadedCall} />}</Show>
      </DataStatus>
    </>
  );
}

interface LaterCardProps extends TestIdProps {
  title: string;
  description: string;
}

/** Room kept for content of a later release. */
function LaterCard(props: LaterCardProps) {
  return (
    <Card
      variant="inlineGrey"
      title={props.title}
      flags={[
        {
          label: i18n.t('page.telephony.callDetail.later'),
          colorScheme: 'neutral',
          isSubtle: true,
        },
      ]}
      data-testid={props['data-testid']}
    >
      <Text size="small" color="tertiary">
        {props.description}
      </Text>
    </Card>
  );
}

interface TelephonyCallDetailLoadedProps {
  call: TelephonyCallDetailModel;
}

function TelephonyCallDetailLoaded(props: TelephonyCallDetailLoadedProps) {
  const navigate = useNavigate();
  const formatDateTime = useDateTimeFormatter();
  const [canAssignRecord, canUnassignRecord, canUpdateNote] = usePermissions({
    permissionKeys: ['callAssignRecord', 'callUnassignRecord', 'callUpdateNote'],
  });

  const call = props.call;
  const isInbound = call.direction === 'inbound';
  const isMissed = isMissedTelephonyCallOutcome(call.outcome);
  const phoneNumber = isNotNil(call.phoneNumber) ? formatPhoneNumber(call.phoneNumber) : null;
  const title =
    call.customer?.name ?? phoneNumber ?? i18n.t('page.telephony.callDetail.withheldNumber');

  const outcomeColorScheme = match({isMissed, outcome: call.outcome})
    .returnType<EntityHeaderFlag['colorScheme']>()
    .with({isMissed: true}, () => 'red')
    .with({outcome: 'bot'}, () => 'neutral')
    .otherwise(() => 'green');

  const flags = buildArray<EntityHeaderFlag>()
    .add({
      label: isInbound
        ? i18n.t('entity.activityPanel.activity.inboundCall')
        : i18n.t('entity.activityPanel.activity.outboundCall'),
      colorScheme: 'blue',
      isSubtle: true,
    })
    .add({
      label: getTelephonyCallOutcomeLabel(call.outcome),
      colorScheme: outcomeColorScheme,
      isSubtle: true,
    })
    .when(isEmpty(call.records), {
      label: i18n.t('page.telephony.callDetail.notLinked'),
      colorScheme: 'orange',
      isSubtle: true,
    });

  const {customer, user, durationSeconds} = call;

  const parameters = buildArray<Parameter>()
    // The number is the title when no customer name is known.
    .whenNotNil(isNotNil(customer?.name) ? phoneNumber : null)
    .add(`${call.line.name} ${formatPhoneNumber(call.line.phoneNumber) ?? call.line.phoneNumber}`)
    .whenNotNil(user, (callUser) =>
      isInbound
        ? i18n.t('page.telephony.indicator.answeredBy', {name: callUser.name})
        : callUser.name
    )
    .whenNotNil(durationSeconds, (seconds) => formatCallDuration(seconds))
    .add(formatDateTime('dateTimeShort', parseDate(call.startedAt)));

  const actions: EntityHeaderAction[] = isNotNil(customer)
    ? [
        {
          type: 'button',
          variant: 'secondary',
          leftIcon: 'action/open_in_new',
          title: i18n.t('page.telephony.indicator.openCustomer'),
          onClick: () => navigate(composePath(customerRoutes.detail, {params: {id: customer.id}})),
          'data-testid': testIds.telephony.callDetail('openCustomer'),
        },
      ]
    : [];

  return (
    <Page
      header={
        <EntityHeader
          icon="communication/call"
          flags={flags}
          title={title}
          parameters={parameters}
          actions={actions}
          data-testid={testIds.telephony.callDetail('header')}
        />
      }
      data-testid={testIds.telephony.callDetail('page')}
    >
      <Box padding={4} width="100%">
        <VStack spacing={4} width="100%">
          <Stack direction={['column', 'column', 'row']} spacing={4} align="stretch" width="100%">
            <Box flex={MAIN_COLUMN_FLEX} minWidth={0}>
              <VStack spacing={4}>
                <CallCustomerCard
                  call={call}
                  data-testid={testIds.telephony.callDetail('customer')}
                />
                <CallLinkedRecordsCard
                  call={call}
                  canLink={canAssignRecord}
                  canUnlink={canUnassignRecord}
                  data-testid={testIds.telephony.callDetail('records')}
                />
                <Card
                  title={i18n.t('general.labels.note')}
                  data-testid={testIds.telephony.callDetail('noteCard')}
                >
                  <TelephonyCallNoteEditor
                    callId={call.id}
                    serverNote={call.note}
                    isDisabled={!canUpdateNote}
                    data-testid={testIds.telephony.callDetail('note')}
                  />
                </Card>
              </VStack>
            </Box>
            <Box flex={SIDE_COLUMN_FLEX} minWidth={0}>
              <VStack spacing={4}>
                <CallTimeline call={call} data-testid={testIds.telephony.callDetail('timeline')} />
                <LaterCard
                  title={i18n.t('page.telephony.callDetail.recording')}
                  description={i18n.t('page.telephony.callDetail.recordingLater')}
                  data-testid={testIds.telephony.callDetail('recording')}
                />
                <LaterCard
                  title={i18n.t('page.telephony.callDetail.transcript')}
                  description={i18n.t('page.telephony.callDetail.transcriptLater')}
                  data-testid={testIds.telephony.callDetail('transcript')}
                />
              </VStack>
            </Box>
          </Stack>
        </VStack>
      </Box>
    </Page>
  );
}
